import { Navigate, type RouteObject } from 'react-router';
import type { Ability } from '@/auth/abilities';
import { BootScreen, RequireAuth } from '@/auth/RequireAuth';
import { AppShell } from '@/components/layout/AppShell';
import { BlankScreen, NotFoundPage, RouteError } from './RouteError';

/** What a console route requires; the menu entry for the same path must ask for the same ability. */
export interface RouteHandle {
  ability?: Ability;
}

/**
 * Every console route and the ability that gates it (`RouteGate` renders
 * No access, or sends the landing page to the first allowed destination).
 * `navigation-access.test.ts` keeps the menu in step with these gates.
 * Route-level code splitting: every page is its own chunk.
 */
export const routes: RouteObject[] = [
  {
    path: '/login',
    HydrateFallback: BootScreen,
    lazy: async () => ({ Component: (await import('@/features/auth/LoginPage')).LoginPage }),
  },
  {
    // The customer-facing acceptance page: no session, no console chrome.
    path: '/q/:token',
    HydrateFallback: BlankScreen,
    ErrorBoundary: RouteError,
    lazy: async () => ({ Component: (await import('@/features/public/PublicQuotePage')).PublicQuotePage }),
  },
  {
    // Biller's contract signing page: the signing link is the credential.
    path: '/sign/:token',
    HydrateFallback: BlankScreen,
    ErrorBoundary: RouteError,
    lazy: async () => ({ Component: (await import('@/features/public/SigningPage')).SigningPage }),
  },
  {
    // The hosted customer portal: a customer's own session, no console chrome.
    path: '/portal/:token',
    HydrateFallback: BlankScreen,
    lazy: async () => ({ Component: (await import('@/features/portal/PortalEntry')).PortalEntry }),
  },
  {
    path: '/portal',
    HydrateFallback: BlankScreen,
    ErrorBoundary: RouteError,
    lazy: async () => ({ Component: (await import('@/features/portal/PortalPage')).PortalPage }),
  },
  {
    // The affiliate portal: the participant's own session, no console chrome.
    path: '/affiliate',
    HydrateFallback: BlankScreen,
    ErrorBoundary: RouteError,
    lazy: async () => ({
      Component: (await import('@/features/affiliate/AffiliatePortalPage')).AffiliatePortalPage,
    }),
  },
  {
    HydrateFallback: BootScreen,
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      {
        ErrorBoundary: RouteError,
        children: [
          {
            index: true,
            handle: { ability: 'reports:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/overview/OverviewPage')).OverviewPage,
            }),
          },

          {
            path: 'analytics',
            handle: { ability: 'reports:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/analytics/AnalyticsPage')).AnalyticsPage,
            }),
          },
          {
            path: 'revenue',
            handle: { ability: 'reports:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/revenue/RevenuePage')).RevenuePage,
            }),
          },
          {
            path: 'customers',
            handle: { ability: 'customers:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/customers/CustomersPage')).CustomersPage,
            }),
          },
          {
            path: 'customers/:id',
            handle: { ability: 'customers:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/customers/CustomerDetailPage')).CustomerDetailPage,
            }),
          },

          {
            path: 'quotes',
            handle: { ability: 'quotes:read' } satisfies RouteHandle,
            lazy: async () => ({ Component: (await import('@/features/quotes/QuotesPage')).QuotesPage }),
          },
          {
            path: 'quotes/:id',
            handle: { ability: 'quotes:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/quotes/QuoteDetailPage')).QuoteDetailPage,
            }),
          },

          {
            path: 'contracts',
            handle: { ability: 'contracts:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/contracts/ContractsPage')).ContractsPage,
            }),
          },
          {
            path: 'contracts/:id',
            handle: { ability: 'contracts:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/contracts/ContractDetailPage')).ContractDetailPage,
            }),
          },

          {
            path: 'subscriptions',
            handle: { ability: 'subscriptions:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/subscriptions/SubscriptionsPage')).SubscriptionsPage,
            }),
          },
          {
            path: 'subscriptions/:id',
            handle: { ability: 'subscriptions:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/subscriptions/SubscriptionDetailPage'))
                .SubscriptionDetailPage,
            }),
          },

          {
            path: 'invoices',
            handle: { ability: 'invoices:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/invoices/InvoicesPage')).InvoicesPage,
            }),
          },
          {
            path: 'invoices/:id',
            handle: { ability: 'invoices:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/invoices/InvoiceDetailPage')).InvoiceDetailPage,
            }),
          },

          {
            path: 'payments',
            handle: { ability: 'payments:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/payments/PaymentsPage')).PaymentsPage,
            }),
          },
          {
            path: 'bank-reconciliation',
            handle: { ability: 'payments:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/bank/BankReconciliationPage')).BankReconciliationPage,
            }),
          },
          {
            path: 'payments/:id',
            handle: { ability: 'payments:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/payments/PaymentDetailPage')).PaymentDetailPage,
            }),
          },

          {
            path: 'receivables/aging',
            handle: { ability: 'receivables:read' } satisfies RouteHandle,
            lazy: async () => ({ Component: (await import('@/features/receivables/AgingPage')).AgingPage }),
          },
          {
            path: 'receivables/collections',
            handle: { ability: 'receivables:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/receivables/CollectionsPage')).CollectionsPage,
            }),
          },
          {
            path: 'receivables/statements',
            handle: { ability: 'receivables:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/receivables/StatementsPage')).StatementsPage,
            }),
          },
          {
            path: 'receivables/payment-plans',
            handle: { ability: 'receivables:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/receivables/PaymentPlansPage')).PaymentPlansPage,
            }),
          },

          {
            path: 'catalog/products',
            handle: { ability: 'catalog:read' } satisfies RouteHandle,
            lazy: async () => ({ Component: (await import('@/features/catalog/ProductsPage')).ProductsPage }),
          },
          {
            path: 'catalog/products/:id',
            handle: { ability: 'catalog:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/catalog/ProductDetailPage')).ProductDetailPage,
            }),
          },
          {
            path: 'catalog/prices',
            handle: { ability: 'catalog:read' } satisfies RouteHandle,
            lazy: async () => ({ Component: (await import('@/features/catalog/PricesPage')).PricesPage }),
          },
          {
            path: 'catalog/tax-rates',
            handle: { ability: 'catalog:read' } satisfies RouteHandle,
            lazy: async () => ({ Component: (await import('@/features/catalog/TaxRatesPage')).TaxRatesPage }),
          },
          {
            path: 'catalog/features',
            handle: { ability: 'catalog:read' } satisfies RouteHandle,
            lazy: async () => ({ Component: (await import('@/features/catalog/FeaturesPage')).FeaturesPage }),
          },
          {
            path: 'catalog/meters',
            handle: { ability: 'catalog:read' } satisfies RouteHandle,
            lazy: async () => ({ Component: (await import('@/features/catalog/MetersPage')).MetersPage }),
          },

          {
            path: 'developers/api-keys',
            handle: { ability: 'api_keys:manage' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/developers/ApiKeysPage')).ApiKeysPage,
            }),
          },
          {
            path: 'developers/webhooks',
            handle: { ability: 'webhooks:manage' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/developers/WebhooksPage')).WebhooksPage,
            }),
          },
          {
            path: 'developers/webhooks/:id',
            handle: { ability: 'webhooks:manage' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/developers/WebhookDetailPage')).WebhookDetailPage,
            }),
          },
          {
            path: 'developers/embed',
            handle: { ability: 'customers:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/developers/EmbedPage')).EmbedPage,
            }),
          },
          {
            path: 'developers/events',
            handle: { ability: 'webhooks:manage' } satisfies RouteHandle,
            lazy: async () => ({ Component: (await import('@/features/developers/EventsPage')).EventsPage }),
          },
          {
            path: 'developers/audit-log',
            handle: { ability: 'audit:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/developers/AuditLogPage')).AuditLogPage,
            }),
          },

          {
            path: 'partners',
            handle: { ability: 'customers:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/partners/PartnersPage')).PartnersPage,
            }),
          },
          {
            path: 'partners/:id',
            handle: { ability: 'customers:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/partners/PartnerDetailPage')).PartnerDetailPage,
            }),
          },
          {
            path: 'commissions',
            handle: { ability: 'commissions:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/commissions/CommissionsPage')).CommissionsPage,
            }),
          },
          {
            path: 'commissions/participants',
            handle: { ability: 'commissions:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/commissions/ParticipantsPage')).ParticipantsPage,
            }),
          },
          {
            path: 'commissions/participants/:id',
            handle: { ability: 'commissions:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/commissions/ParticipantDetailPage')).ParticipantDetailPage,
            }),
          },
          {
            path: 'commissions/payouts',
            handle: { ability: 'commissions:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/commissions/PayoutsPage')).PayoutsPage,
            }),
          },
          {
            path: 'commissions/periods',
            handle: { ability: 'commissions:read' } satisfies RouteHandle,
            lazy: async () => ({
              Component: (await import('@/features/commissions/PeriodsPage')).PeriodsPage,
            }),
          },

          {
            path: 'ledger',
            handle: { ability: 'reports:read' } satisfies RouteHandle,
            lazy: async () => ({ Component: (await import('@/features/settings/LedgerPage')).LedgerPage }),
          },
          {
            // The ledger's address before it moved out of settings.
            path: 'settings/ledger',
            handle: { ability: 'reports:read' } satisfies RouteHandle,
            element: <Navigate to="/ledger" replace />,
          },

          {
            // The settings area: these pages share its menu (`SettingsLayout`).
            lazy: async () => ({
              Component: (await import('@/features/settings/SettingsLayout')).SettingsLayout,
            }),
            children: [
              {
                path: 'settings/team',
                handle: { ability: 'team:manage' } satisfies RouteHandle,
                lazy: async () => ({ Component: (await import('@/features/settings/TeamPage')).TeamPage }),
              },
              {
                path: 'settings/gateways',
                handle: { ability: 'gateways:manage' } satisfies RouteHandle,
                lazy: async () => ({
                  Component: (await import('@/features/settings/GatewaysPage')).GatewaysPage,
                }),
              },
              {
                path: 'settings/collection-policies',
                handle: { ability: 'receivables:read' } satisfies RouteHandle,
                lazy: async () => ({
                  Component: (await import('@/features/settings/CollectionPoliciesPage'))
                    .CollectionPoliciesPage,
                }),
              },
              {
                path: 'settings/accounting',
                handle: { ability: 'settings:manage' } satisfies RouteHandle,
                lazy: async () => ({
                  Component: (await import('@/features/settings/AccountingPage')).AccountingPage,
                }),
              },
              {
                path: 'settings/automation',
                handle: { ability: 'settings:manage' } satisfies RouteHandle,
                lazy: async () => ({
                  Component: (await import('@/features/settings/AutomationRulesPage')).AutomationRulesPage,
                }),
              },
              {
                path: 'settings/crm',
                handle: { ability: 'settings:manage' } satisfies RouteHandle,
                lazy: async () => ({
                  Component: (await import('@/features/settings/CrmPage')).CrmPage,
                }),
              },
              {
                path: 'settings/e-signatures',
                handle: { ability: 'settings:manage' } satisfies RouteHandle,
                lazy: async () => ({
                  Component: (await import('@/features/settings/ESignaturesPage')).ESignaturesPage,
                }),
              },
              {
                path: 'settings/legal-entities',
                handle: { ability: 'settings:manage' } satisfies RouteHandle,
                lazy: async () => ({
                  Component: (await import('@/features/settings/LegalEntitiesPage')).LegalEntitiesPage,
                }),
              },
              {
                path: 'settings/approvals',
                handle: { ability: 'quotes:read' } satisfies RouteHandle,
                lazy: async () => ({
                  Component: (await import('@/features/settings/ApprovalRulesPage')).ApprovalRulesPage,
                }),
              },
              {
                path: 'settings/commissions',
                handle: { ability: 'commissions:read' } satisfies RouteHandle,
                lazy: async () => ({
                  Component: (await import('@/features/settings/CommissionSettingsPage'))
                    .CommissionSettingsPage,
                }),
              },
              {
                path: 'settings/portal',
                handle: { ability: 'customers:read' } satisfies RouteHandle,
                lazy: async () => ({
                  Component: (await import('@/features/settings/PortalSettingsPage')).PortalSettingsPage,
                }),
              },
            ],
          },

          { path: '*', Component: NotFoundPage },
        ],
      },
    ],
  },
];
